import { useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import makeStyles from '@mui/styles/makeStyles';
import {
  useApi,
  fetchApiRef,
  discoveryApiRef,
  configApiRef,
} from '@backstage/core-plugin-api';
import { useDebounce } from 'react-use';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

/**
 * LeanIX data structure returned from the backend API
 */
export interface LeanIxData {
  valid: boolean;
  message?: string;
  payload: {
    enteredId: string;
    internalId: string;
    displayName: string;
    category: string;
    type: string;
    updatedAt: string;
    factsheetUrl: string;
  } | null;
}

const useStyles = makeStyles(theme => ({
  container: {
    width: '100%',
  },
  actionText: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
    fontWeight: 500,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  leanixInput: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  errorText: {
    color: theme.palette.error.main,
  },
}));

/**
 * Schema for the LeanixPicker field extension
 */
export interface LeanixPickerSchema {
  title?: string;
  description?: string;
}

/**
 * UI Schema options for the LeanixPicker
 */
interface LeanixPickerUiSchema {
  'ui:options'?: {
    /** Placeholder text for the input field */
    placeholder?: string;
    /** Whether to return the factsheetUrl instead of the display value */
    returnFactsheetUrl?: boolean;
    /** Debounce delay in milliseconds for LeanIX validation */
    debounceMs?: number;
  };
}

/**
 * LeanixPicker - A scaffolder field extension for validating and selecting LeanIX IDs
 *
 * This component provides a text field that validates LeanIX IDs against the backend
 * and displays the validated information for use in scaffolder templates.
 */
export const LeanixPicker = (
  props: FieldExtensionComponentProps<string, LeanixPickerSchema>,
) => {
  const { onChange, required, formData, uiSchema, schema } = props;

  const classes = useStyles();
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const configApi = useApi(configApiRef);

  const [inputValue, setInputValue] = useState(formData || '');
  const [debouncedValue, setDebouncedValue] = useState(formData || '');
  const [isCleared, setIsCleared] = useState(false);
  const [validatedData, setValidatedData] = useState<LeanIxData | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const requestIdRef = useRef(0);
  const leanixBaseUrl = configApi.getString('leanix.factsheetBaseUrl');

  // Get UI options with defaults
  const uiOptions = (uiSchema as LeanixPickerUiSchema)?.['ui:options'] ?? {};
  const placeholder = uiOptions.placeholder ?? 'e.g. c123456';
  const returnFactsheetUrl = uiOptions.returnFactsheetUrl ?? false;
  const debounceMs = uiOptions.debounceMs ?? 500;

  useDebounce(
    () => {
      setDebouncedValue(inputValue.trim());
    },
    debounceMs,
    [inputValue, debounceMs],
  );

  // Fetch LeanIX details
  const fetchLeanIxDetailsById = useCallback(
    async (leanIxId: string): Promise<LeanIxData> => {
      const baseUrl = await discoveryApi.getBaseUrl('leanix');
      const response = await fetchApi.fetch(`${baseUrl}/validate/${leanIxId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(
          'Sorry, something went wrong, please contact XELERATE for support',
        );
      }

      const responseData = await response.json();
      return responseData;
    },
    [discoveryApi, fetchApi],
  );

  useEffect(() => {
    const leanIxId = debouncedValue.trim();

    if (!leanIxId) {
      requestIdRef.current += 1;
      setIsValidating(false);
      setIsCleared(true);
      setValidatedData(null);
      setValidationError(null);
      onChange(undefined);
      return;
    }

    setIsCleared(false);
    setIsValidating(true);
    setValidationError(null);
    setValidatedData(null);

    const requestId = ++requestIdRef.current;

    void fetchLeanIxDetailsById(leanIxId)
      .then(leanIxByIdData => {
        if (requestIdRef.current !== requestId) {
          return;
        }

        setIsValidating(false);

        if (leanIxByIdData.valid && leanIxByIdData.payload) {
          setValidatedData(leanIxByIdData);
          setValidationError(null);

          if (returnFactsheetUrl) {
            onChange(leanIxByIdData.payload.factsheetUrl);
          } else {
            onChange(leanIxByIdData.payload.enteredId);
          }
          return;
        }

        setValidatedData(null);
        setValidationError(
          leanIxByIdData.message ||
            'The provided LeanIX ID is invalid or could not be found.',
        );
        onChange(undefined);
      })
      .catch(error => {
        if (requestIdRef.current !== requestId) {
          return;
        }

        setIsValidating(false);
        setValidatedData(null);
        setValidationError(
          error instanceof Error
            ? error.message
            : 'Failed to validate LeanIX ID',
        );
        onChange(undefined);
      });
  }, [debouncedValue, fetchLeanIxDetailsById, onChange, returnFactsheetUrl]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInputValue(value);

    if (value === '') {
      setIsCleared(true);
      setValidatedData(null);
      setValidationError(null);
      setIsValidating(false);
      onChange(undefined);
    } else {
      setIsCleared(false);
      setValidatedData(null);
      setValidationError(null);
      onChange(undefined);
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const trimmedValue = event.target.value.trim();
    setInputValue(trimmedValue);
  };

  // Determine error state
  const isLeanIxError = !isCleared && !isValidating && !!validationError;

  let helperMessage: ReactNode = '';
  if (isValidating) {
    helperMessage = 'Validating LeanIX ID...';
  } else if (isCleared || !inputValue) {
    helperMessage = schema.description || '';
  } else if (validationError) {
    helperMessage = (
      <>
        {validationError}{' '}
        <Link href={leanixBaseUrl} target="_blank" rel="noopener noreferrer">
          CARIAD LeanIX
        </Link>
      </>
    );
  } else if (validatedData && validatedData.valid && validatedData.payload) {
    helperMessage = (
      <Typography
        component="span"
        sx={{ color: 'success.main', fontSize: 'inherit' }}
      >
        ✓ Validated: {validatedData.payload.displayName} (
        {validatedData.payload.type})
      </Typography>
    );
  } else {
    helperMessage = schema.description || '';
  }

  const hasError = isLeanIxError;

  return (
    <Box className={classes.container}>
      <TextField
        fullWidth
        label={schema.title || 'LeanIX ID'}
        placeholder={placeholder}
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        required={required}
        error={hasError}
        helperText={helperMessage}
        InputProps={{
          endAdornment: inputValue ? (
            <InputAdornment position="end" className={classes.leanixInput}>
              {isValidating && (
                <CircularProgress color="inherit" size="1.25rem" />
              )}
            </InputAdornment>
          ) : null,
        }}
      />
    </Box>
  );
};
